import type { SubscriptionType } from '@kanbu/schema';
import { Currency } from '@kanbu/schema/enums';
import {
  convertCreditsToCurrency,
  convertCurrencyToCredits,
  formatUtils,
} from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label, Switch, toast } from '@utima/ui';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';

/**
 * Values passed to `onSave`, in **base credits** (already converted from the
 * display currency). Matches both `subscriptions.update` (self-serve) and
 * `subscriptions.updateAutoRenewal` (plan-management) inputs.
 */
export interface AutoRenewalUpdateValues {
  autoRenewal: boolean;
  autoRenewalAmount?: number;
  autoRenewalLimit?: number;
  autoRenewalThreshold?: number;
}

interface AutoRenewalSettingsProps {
  subscription: SubscriptionType;
  currency: Currency;
  /** Persist handler — the parent wires it to the appropriate scoped mutation. */
  onSave: (values: AutoRenewalUpdateValues) => Promise<void>;
  isSaving: boolean;
  onUpdate?: () => void;
}

const MIN_AMOUNT = 5; // Credits (USD amount)
const CurrencySymbols = Object.freeze({
  [Currency.USD]: '$',
  [Currency.CZK]: 'Kč',
});

export function AutoRenewalSettings({
  subscription,
  currency,
  onSave,
  isSaving,
  onUpdate,
}: AutoRenewalSettingsProps) {
  const { t } = useLingui();
  const currencySymbol = CurrencySymbols[currency];
  const currencyMinAmount = convertCreditsToCurrency(MIN_AMOUNT, currency);

  const isLoading = isSaving;

  const [autoRenewal, setAutoRenewal] = useState<boolean>(
    subscription.autoRenewal ?? false,
  );
  const [autoRenewalAmount, setAutoRenewalAmount] = useState<number | null>(
    (subscription.autoRenewalAmount &&
      convertCreditsToCurrency(subscription.autoRenewalAmount, currency)) ??
      null,
  );
  const [autoRenewalLimit, setAutoRenewalLimit] = useState<number | null>(
    (subscription.autoRenewalLimit &&
      convertCreditsToCurrency(subscription.autoRenewalLimit, currency)) ??
      null,
  );
  const [limitMonthly, setLimitMonthly] = useState(
    subscription.autoRenewalLimit !== null,
  );
  const [autoRenewalThreshold, setAutoRenewalThreshold] = useState<
    number | null
  >(
    (subscription.autoRenewalThreshold &&
      convertCreditsToCurrency(subscription.autoRenewalThreshold, currency)) ??
      currencyMinAmount,
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isThresholdInvalid, setIsThresholdInvalid] = useState(
    autoRenewalThreshold !== null && autoRenewalThreshold < currencyMinAmount,
  );

  useEffect(() => {
    const currentMinAmount = convertCreditsToCurrency(5, currency);
    const currentThreshold =
      (subscription.autoRenewalThreshold &&
        convertCreditsToCurrency(
          subscription.autoRenewalThreshold,
          currency,
        )) ??
      currentMinAmount;

    setAutoRenewal(subscription.autoRenewal ?? false);
    setAutoRenewalAmount(
      (subscription.autoRenewalAmount &&
        convertCreditsToCurrency(subscription.autoRenewalAmount, currency)) ??
        null,
    );
    setAutoRenewalLimit(
      (subscription.autoRenewalLimit &&
        convertCreditsToCurrency(subscription.autoRenewalLimit, currency)) ??
        null,
    );
    setLimitMonthly(subscription.autoRenewalLimit !== null);
    setAutoRenewalThreshold(currentThreshold);
    setIsThresholdInvalid(currentThreshold < currentMinAmount);
    setHasUnsavedChanges(false);
  }, [subscription, currency]);

  const handleAutoRenewalToggle = (newValue: boolean) => {
    setAutoRenewal(newValue);

    if (newValue) {
      if (!autoRenewalAmount || autoRenewalAmount < currencyMinAmount) {
        setAutoRenewalAmount(currencyMinAmount);
      }
      if (!autoRenewalThreshold || autoRenewalThreshold < currencyMinAmount) {
        setAutoRenewalThreshold(currencyMinAmount);
        setIsThresholdInvalid(false);
      }
    }

    setHasUnsavedChanges(true);
  };

  const handleAmountChange = (value: string) => {
    const num = Number.parseFloat(value);
    setAutoRenewalAmount(Number.isNaN(num) ? null : num);
    setHasUnsavedChanges(true);
  };

  const handleLimitChange = (value: string) => {
    const num = Number.parseFloat(value);
    setAutoRenewalLimit(Number.isNaN(num) || num < 0 ? null : num);
    setHasUnsavedChanges(true);
  };

  const handleLimitToggle = (checked: boolean) => {
    setLimitMonthly(checked);
    if (!checked) {
      setAutoRenewalLimit(null);
    }
    setHasUnsavedChanges(true);
  };

  const handleThresholdChange = (value: string) => {
    const num = Number.parseFloat(value);
    const isValidNumber = !Number.isNaN(num);
    const meetsMinimum = isValidNumber && num >= currencyMinAmount;

    setAutoRenewalThreshold(isValidNumber ? num : null);
    setIsThresholdInvalid(isValidNumber && !meetsMinimum);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (autoRenewal && isThresholdInvalid) {
      toast.error(
        t`Renewal Threshold must be at least ${formatUtils.currency(currencyMinAmount, currency)} when enabled.`,
      );

      return;
    }
    if (
      autoRenewal &&
      (!autoRenewalThreshold || autoRenewalThreshold < currencyMinAmount)
    ) {
      toast.error(
        t`Renewal Threshold must be at least ${formatUtils.currency(currencyMinAmount, currency)} when enabled.`,
      );

      return;
    }
    if (
      autoRenewal &&
      (!autoRenewalAmount || autoRenewalAmount < currencyMinAmount)
    ) {
      toast.error(
        t`Renewal Amount must be at least ${formatUtils.currency(currencyMinAmount, currency)} when enabled.`,
      );

      return;
    }
    if (
      autoRenewal &&
      limitMonthly &&
      (autoRenewalLimit === null || autoRenewalLimit < 0)
    ) {
      toast.error(
        t`Monthly Limit must be ${currencySymbol}0 or greater when enabled.`,
      );

      return;
    }

    try {
      /**
       * The values have to be converted to a credits
       * before sending to the backend.
       */
      await onSave({
        autoRenewal: autoRenewal,
        autoRenewalAmount:
          autoRenewal && autoRenewalAmount
            ? convertCurrencyToCredits(autoRenewalAmount, currency)
            : undefined,
        autoRenewalLimit:
          autoRenewal && limitMonthly && autoRenewalLimit
            ? convertCurrencyToCredits(autoRenewalLimit, currency)
            : undefined,
        autoRenewalThreshold:
          autoRenewal && autoRenewalThreshold
            ? convertCurrencyToCredits(autoRenewalThreshold, currency)
            : undefined,
      });

      setHasUnsavedChanges(false);
      setIsThresholdInvalid(false);
      toast.success(t`Auto-renewal settings updated.`);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to update auto-renewal settings:', error);
      toast.error(t`Failed to save settings.`);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-start justify-between'>
        <Label
          htmlFor='auto-renewal-switch'
          className='flex flex-col space-y-1'
        >
          <span>
            <Trans>Enable Auto-Renewal</Trans>
          </span>
          <span className='text-xs text-gray-500'>
            <Trans>
              Automatically adds the &apos;Renewal Amount&apos; to your prepaid
              balance when it&apos;s too low to cover usage costs.
            </Trans>
          </span>
        </Label>
        <Switch
          id='auto-renewal-switch'
          checked={autoRenewal}
          onCheckedChange={handleAutoRenewalToggle}
          disabled={isLoading}
        />
      </div>

      {autoRenewal && (
        <div className='space-y-3 border-t pt-4'>
          <div className='space-y-1'>
            <Label htmlFor='renewal-threshold'>
              <Trans>When balance goes below ({currencySymbol})</Trans>
            </Label>
            <Input
              id='renewal-threshold'
              type='number'
              step='0.01'
              min={currencyMinAmount}
              value={autoRenewalThreshold ?? ''}
              onChange={e => handleThresholdChange(e.target.value)}
              placeholder={t`e.g., ${currencyMinAmount}`}
              disabled={isLoading}
              className={isThresholdInvalid ? 'border-red-500' : ''}
            />
            {isThresholdInvalid && (
              <p className='text-xs text-red-500'>
                <Trans>
                  Minimum threshold is{' '}
                  {formatUtils.currency(currencyMinAmount, currency)}.
                </Trans>
              </p>
            )}
            <p className='text-xs text-gray-500'>
              <Trans>
                Enter an amount{' '}
                {formatUtils.currency(currencyMinAmount, currency)} or above
              </Trans>
            </p>
          </div>
          <div className='space-y-1'>
            <Label htmlFor='renewal-amount'>
              <Trans>Add this amount to balance ({currencySymbol})</Trans>
            </Label>
            <Input
              id='renewal-amount'
              type='number'
              min={currencyMinAmount}
              step='0.01'
              value={autoRenewalAmount ?? ''}
              onChange={e => handleAmountChange(e.target.value)}
              placeholder={t`e.g., ${currencyMinAmount * 4}`}
              disabled={isLoading}
            />
            <p className='text-xs text-gray-500'>
              <Trans>
                Amount ({formatUtils.currency(currencyMinAmount, currency)}{' '}
                minimum) to add to your balance when auto-renewal triggers.
              </Trans>
            </p>
          </div>

          <div className='flex items-start justify-between pt-2'>
            <Label
              htmlFor='limit-monthly-switch'
              className='flex flex-col space-y-1'
            >
              <span>
                <Trans>Limit the amount of recharge per month</Trans>
              </span>
              <span className='text-xs text-gray-500'>
                <Trans>Cap auto-renewal charges per calendar month.</Trans>
              </span>
            </Label>
            <Switch
              id='limit-monthly-switch'
              checked={limitMonthly}
              onCheckedChange={handleLimitToggle}
              disabled={isLoading}
            />
          </div>

          {limitMonthly && (
            <div className='space-y-1'>
              <Label htmlFor='renewal-limit'>
                <Trans>Monthly Limit ({currencySymbol})</Trans>
              </Label>
              <Input
                id='renewal-limit'
                type='number'
                min='0'
                step='0.01'
                value={autoRenewalLimit ?? ''}
                onChange={e => handleLimitChange(e.target.value)}
                placeholder={t`e.g., 100 (0 for no limit)`}
                disabled={isLoading}
              />
              {subscription?.autoRenewalPeriodStart && (
                <div className='mt-1 space-y-0.5 text-xs text-gray-500'>
                  <p>
                    <Trans>Period start:</Trans>{' '}
                    {new Date(
                      subscription.autoRenewalPeriodStart,
                    ).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {hasUnsavedChanges && (
        <div className='flex justify-end border-t pt-4'>
          <Button
            data-cta='admin-auto-renewal-settings-save-button'
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className='mr-2 size-4 animate-spin' />
            ) : null}
            <Trans>Save Auto-Renewal Settings</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}
